import { useMemo, useState } from 'react'
import {
  Eye,
  Loader2,
  RefreshCcw,
  Plus,
  Trash2,
  Share2,
} from 'lucide-react'
import { toast } from 'sonner'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useNoteStyles } from '@/hooks/useNoteStyles'
import { NoteStyleEditor } from '@/components/business/NoteStyleEditor'
import { db, transactWithRetry, ADMIN_ORG_ID, SYSTEM_TEMPLATE_ORG_ID } from '@/lib/db'
import { useProfile } from '@/hooks/useProfile'

export function NoteStylesPage() {
  const { profile, isLoading: isProfileLoading } = useProfile()
  const {
    styles,
    templateMap,
    slotMap,
    isLoading: isStylesLoading,
    deleteStyle,
    cloneStyle,
  } = useNoteStyles()

  const [detailOpen, setDetailOpen] = useState(false)
  const [currentStyleId, setCurrentStyleId] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [isCloning, setIsCloning] = useState<string | null>(null)

  const isLoading = isProfileLoading || isStylesLoading
  const isAdmin = profile?.org_id === ADMIN_ORG_ID
  const hasData = styles.length > 0

  const styleSummaries = useMemo(() => {
    return styles.map((style) => {
      const templates = templateMap[style.id] || []
      const slotCount = templates.reduce((sum, tpl) => {
        return sum + (slotMap[tpl.id]?.length || 0)
      }, 0)
      return {
        ...style,
        templateCount: templates.length,
        slotCount,
      }
    })
  }, [styles, templateMap, slotMap])

  const handleOpenDetail = (styleId: string) => {
    setCurrentStyleId(styleId)
    setDetailOpen(true)
  }

  const handleCloseDetail = () => {
    setDetailOpen(false)
    setCurrentStyleId(null)
  }

  const handleCreateStyle = async () => {
    if (isCreating) return
    setIsCreating(true)
    try {
      const newId = crypto.randomUUID()
      await transactWithRetry(
        db.tx.note_styles[newId].update({
          id: newId,
          org_id: profile?.org_id,
          name: '新风格',
          description: '请完善风格描述',
          reference_images: [],
          created_at: Date.now(),
        })
      )
      toast.success('已创建空白风格')
      setCurrentStyleId(newId)
      setDetailOpen(true)
    } catch (error) {
      console.error(error)
      toast.error('创建风格失败，请稍后重试')
    } finally {
      setIsCreating(false)
    }
  }

  const handleDeleteStyle = async (styleId: string, styleName: string, templateCount: number, slotCount: number) => {
    const confirmMsg = `确定要删除风格「${styleName}」吗？\n\n⚠️ 这将同时删除：\n- ${templateCount} 个配图模板\n- ${slotCount} 个配图类型\n\n此操作不可恢复！`
    
    if (!confirm(confirmMsg)) return

    try {
      await deleteStyle(styleId)
      toast.success('风格已删除')
    } catch (error) {
      console.error(error)
      toast.error('删除失败，请稍后重试')
    }
  }

  const handleCloneToWorkbench = async (styleId: string, styleName: string) => {
    if (!confirm(`确定要把风格「${styleName}」复制到模板工作台吗？\n\n这会作为一个新的全局模板提供给所有新企业。`)) {
      return
    }

    setIsCloning(styleId)
    try {
      await cloneStyle(styleId, SYSTEM_TEMPLATE_ORG_ID)
      toast.success('已复制到模板工作台')
    } catch (error) {
      console.error(error)
      toast.error('复制失败，请检查网络或权限')
    } finally {
      setIsCloning(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold">笔记风格</h1>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-muted-foreground text-sm">
          管理系统中的笔记风格、模版及其配图要求。
        </p>
        <Button size="sm" className="gap-2" onClick={handleCreateStyle} disabled={isCreating}>
          {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          新增风格
        </Button>
      </div>

      {isLoading ? (
        <div className="h-64 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : !hasData ? (
        <Card className="p-8 border-dashed text-center space-y-3">
          <div className="flex justify-center">
            <RefreshCcw className="w-8 h-8 text-muted-foreground" />
          </div>
          <p className="text-muted-foreground">暂无风格数据，请在配置完成后再试。</p>
        </Card>
      ) : (
        <div className="grid gap-6">
          {styleSummaries.map((style) => (
            <Card key={style.id} className="hover:shadow-md hover:border-primary/20 hover:-translate-y-0.5 transition-all bg-white/70">
              <CardHeader className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="space-y-3">
                    <CardTitle className="text-lg font-bold">{style.name}</CardTitle>
                    <p className="text-sm text-muted-foreground line-clamp-2 max-w-2xl">{style.description}</p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary" className="bg-secondary/50 text-secondary-foreground border-transparent rounded-full px-2.5 py-0 h-5 text-[10px] font-normal">模版 {style.templateCount} 个</Badge>
                      <Badge variant="secondary" className="bg-secondary/50 text-secondary-foreground border-transparent rounded-full px-2.5 py-0 h-5 text-[10px] font-normal">配图类型 {style.slotCount} 个</Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2 text-primary border-primary/20 hover:bg-primary/5 rounded-xl"
                        onClick={() => handleCloneToWorkbench(style.id, style.name)}
                        disabled={isCloning === style.id}
                      >
                        {isCloning === style.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Share2 className="w-3.5 h-3.5" />
                        )}
                        复制到模板库
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 rounded-xl"
                      onClick={() => handleOpenDetail(style.id)}
                    >
                      <Eye className="w-3.5 h-3.5" />
                      查看详情与编辑
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/20 rounded-xl"
                      onClick={() => handleDeleteStyle(style.id, style.name, style.templateCount, style.slotCount)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      删除
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {/* 详情编辑弹窗 */}
      <NoteStyleEditor 
        styleId={currentStyleId}
        open={detailOpen}
        onClose={handleCloseDetail}
      />
    </div>
  )
}
